import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { schoolClassSortOrder } from '../services/TimetableService';
import './AdminClassesView.css';
import './AdminDetailPages.css';
import { scopeForAdmin, adminSchoolIds } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';

function AdminClassesView({ adminData }) {
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editClass, setEditClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSubjectTeacherModal, setShowSubjectTeacherModal] = useState(false);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [subjectTeacherAssignments, setSubjectTeacherAssignments] = useState({});
  const [detailClass, setDetailClass] = useState(null);
  const [detailStudents, setDetailStudents] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const [newClass, setNewClass] = useState({
    name: '',
    grade: '',
    teacherId: '',
    requiredSubjects: []
  });

  const availableSubjects = ["Mathematics", "English", "Science", "Biology", "Chemistry", "Social Studies", "History", "Spanish", "French", "Information Technology", "Physical Education", "Arts", "Agriculture", "Principles of Accounts (POA)", "Principles of Business (POB)", "Building Technology", "Home Economics", "Woodwork", "Reading Comprehension", "Penmanship"];

  const gradeLevels = [
    "Kindergarten", "Kind B",
    "Grade 1", "Grade 1 B",
    "Grade 2", "Grade 2 B",
    "Grade 3", "Grade 3 B",
    "Grade 4", "Grade 4 B",
    "Grade 5", "Grade 5 B",
    "Grade 6", "Grade 6 B",
    "Form 1", "Form 2", "Form 3", "Form 4", "Form 5", "Form 6"
  ];

  // School-scoped cache key builder — shared with AdminAnalyticsView so tabs
  // reuse each other's fetched collections.
  const ck = (p) => `${adminSchoolIds(adminData).join('|') || 'all'}:${p}`;

  const loadClasses = useCallback(async () => {
    console.log('🔥 AdminClasses: Loading classes, schoolId:', adminData?.schoolId);
    try {
      let classesData = await dataCacheService.getDocs(ck('classes'), collection(db, 'classes'));
      classesData = scopeForAdmin(classesData, adminData);
      classesData.sort(schoolClassSortOrder);
      console.log('🔥 AdminClasses: Classes loaded', classesData.length, classesData);
      setClasses(classesData);
    } catch (error) {
      console.error('🔥 AdminClasses: Error loading classes:', error);
    } finally {
      setLoading(false);
    }
  }, [adminData?.schoolId]);

  const loadTeachers = useCallback(async () => {
    console.log('🔥 AdminClasses: Loading teachers, schoolId:', adminData?.schoolId);
    try {
      const usersData = scopeForAdmin(
        await dataCacheService.getDocs(ck('users'), collection(db, 'users')),
        adminData
      );
      // Show ALL teachers, not just regular type
      const teachersData = usersData.filter(user => user.role === 'Teacher');
      console.log('🔥 AdminClasses: Teachers loaded', teachersData.length, teachersData);
      setTeachers(teachersData);
    } catch (error) {
      console.error('🔥 AdminClasses: Error loading teachers:', error);
    }
  }, [adminData?.schoolId]);

  const loadSubjects = useCallback(async () => {
    console.log('🔥 AdminClasses: Loading subjects, schoolId:', adminData?.schoolId);
    try {
      const allSubjects = [];
      const seenIds = new Set();

      // 1. Load from top-level subjects collection (school-wide / global subjects)
      const globalDocs = scopeForAdmin(
        await dataCacheService.getDocs(ck('subjects'), collection(db, 'subjects')),
        adminData
      );
      globalDocs.forEach(data => {
        if (!seenIds.has(data.id)) { seenIds.add(data.id); allSubjects.push(data); }
      });

      // 2. Load from classes/{classId}/subjects subcollections (parallel, cached)
      const classDocs = scopeForAdmin(
        await dataCacheService.getDocs(ck('classes'), collection(db, 'classes')),
        adminData
      );
      const classSubDocs = await Promise.all(classDocs.map(c =>
        dataCacheService.getDocs(ck(`classes/${c.id}/subjects`), collection(db, 'classes', c.id, 'subjects'))
      ));
      classSubDocs.flat().forEach(data => {
        if (!seenIds.has(data.id)) { seenIds.add(data.id); allSubjects.push(data); }
      });

      // 3. Load from gradeLevels/{gradeId}/subjects subcollections (parallel, cached)
      const gradeDocs = scopeForAdmin(
        await dataCacheService.getDocs(ck('gradeLevels'), collection(db, 'gradeLevels')),
        adminData
      );
      const gradeSubDocs = await Promise.all(gradeDocs.map(g =>
        dataCacheService.getDocs(ck(`gradeLevels/${g.id}/subjects`), collection(db, 'gradeLevels', g.id, 'subjects'))
      ));
      gradeSubDocs.flat().forEach(data => {
        if (!seenIds.has(data.id)) { seenIds.add(data.id); allSubjects.push(data); }
      });

      console.log('🔥 AdminClasses: Subjects loaded', allSubjects.length, allSubjects);
      setSubjects(allSubjects);
    } catch (error) {
      console.error('🔥 AdminClasses: Error loading subjects:', error);
    }
  }, [adminData?.schoolId]);

  useEffect(() => {
    loadClasses();
    loadTeachers();
    loadSubjects();
  }, [loadClasses, loadTeachers, loadSubjects]);

  const handleAddClass = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'classes'), {
        ...newClass,
        isActive: true,
        createdAt: new Date().toISOString(),
        requiredSubjects: newClass.requiredSubjects || [],
        subjectTeacherAssignments: {},
        schoolId: adminData?.schoolId || null
      });
      dataCacheService.invalidatePath('classes');
      setShowAddForm(false);
      setNewClass({ name: '', grade: '', teacherId: '', requiredSubjects: [] });
      loadClasses();
    } catch (error) {
      console.error('Error adding class:', error);
      alert('Failed to add class');
    }
  };

  const handleDeleteClass = async (classId) => {
    const confirmed = window.confirm(
      'Are you sure you want to PERMANENTLY DELETE this class?\n\nAll students, subjects, assignments, and grades within this class will also be deleted. This action CANNOT be undone.'
    );
    if (!confirmed) return false;
    try {
      // Delete all students in the class subcollection first
      const studentsSnapshot = await getDocs(collection(db, 'classes', classId, 'students'));
      for (const studentDoc of studentsSnapshot.docs) {
        await deleteDoc(doc(db, 'classes', classId, 'students', studentDoc.id));
      }
      // Delete all subjects
      const subjectsSnapshot = await getDocs(collection(db, 'classes', classId, 'subjects'));
      for (const subjectDoc of subjectsSnapshot.docs) {
        await deleteDoc(doc(db, 'classes', classId, 'subjects', subjectDoc.id));
      }
      // Delete the class document itself
      await deleteDoc(doc(db, 'classes', classId));
      dataCacheService.invalidatePath('classes');
      loadClasses();
      return true;
    } catch (error) {
      console.error('Error deleting class:', error);
      alert('Failed to delete class: ' + error.message);
      return false;
    }
  };

  const handleCloseModal = () => {
    setIsEditing(false);
    setEditClass(null);
  };

  const handleUpdateClass = async (e) => {
    e.preventDefault();
    if (!editClass) return;
    try {
      await updateDoc(doc(db, 'classes', editClass.id), {
        name: editClass.name,
        grade: editClass.grade,
        teacherId: editClass.teacherId || null,
        updatedAt: new Date().toISOString()
      });
      dataCacheService.invalidatePath('classes');
      handleCloseModal();
      loadClasses();
    } catch (error) {
      console.error('Error updating class:', error);
      alert('Failed to update class');
    }
  };

  const handleAssignSubject = async (subjectId) => {
    if (!selectedClass) return;
    try {
      await updateDoc(doc(db, 'subjects', subjectId), {
        classId: selectedClass.id
      });
      dataCacheService.invalidatePath('subjects');
      loadSubjects();
      alert('Subject assigned to class');
    } catch (error) {
      console.error('Error assigning subject:', error);
      alert('Failed to assign subject');
    }
  };

  const handleRemoveSubject = async (subjectId) => {
    if (!selectedClass) return;
    try {
      await updateDoc(doc(db, 'subjects', subjectId), {
        classId: null
      });
      dataCacheService.invalidatePath('subjects');
      loadSubjects();
      alert('Subject removed from class');
    } catch (error) {
      console.error('Error removing subject:', error);
      alert('Failed to remove subject');
    }
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId || t.uid === teacherId);
    if (!teacher) return 'Unassigned';
    const fullName = `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim();
    return fullName || teacher.username || 'Unassigned';
  };

  const openClassDetail = async (cls) => {
    setDetailClass(cls);
    setDetailLoading(true);
    setDetailStudents([]);
    try {
      const list = (await dataCacheService.getDocs(ck(`classes/${cls.id}/students`), collection(db, 'classes', cls.id, 'students')))
        .filter(s => s.isActive !== false);
      list.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
      setDetailStudents(list);
    } catch (e) {
      console.error('Error loading class students:', e);
    } finally {
      setDetailLoading(false);
    }
  };

  const getClassSubjects = () => {
    if (!selectedClass) return [];
    return subjects.filter(s => s.classId === selectedClass.id);
  };

  const getAvailableSubjects = () => {
    if (!selectedClass) return [];
    return subjects.filter(s => s.classId !== selectedClass.id);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Edit modal — shared by the list page and the detail page
  const editClassModal = isEditing && editClass && (
    <div className="modal-overlay" onClick={handleCloseModal}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Edit Class</h3>
        <form onSubmit={handleUpdateClass}>
          <div className="form-group">
            <label>Class Name *</label>
            <input
              type="text"
              value={editClass.name}
              onChange={(e) => setEditClass({ ...editClass, name: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Grade *</label>
            <select
              value={editClass.grade}
              onChange={(e) => setEditClass({ ...editClass, grade: e.target.value })}
              required
            >
              <option value="">Select Grade</option>
              {gradeLevels.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Home Teacher</label>
            <select
              value={editClass.teacherId || ''}
              onChange={(e) => setEditClass({ ...editClass, teacherId: e.target.value })}
            >
              <option value="">Unassigned</option>
              {teachers.map(teacher => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.username}
                </option>
              ))}
            </select>
          </div>
          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={handleCloseModal}>
              Cancel
            </button>
            <button type="submit">Save Changes</button>
          </div>
        </form>
      </div>
    </div>
  );

  // Manage Subjects modal — shared by the list page and the detail page
  const subjectModal = showSubjectModal && selectedClass && (
    <div className="modal-overlay" onClick={() => setShowSubjectModal(false)}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Manage Subjects - {selectedClass.name}</h3>
        <div className="subject-management">
          <h4>Available Subjects</h4>
          {getAvailableSubjects().length === 0 ? (
            <p className="no-data">No available subjects to assign</p>
          ) : (
            <div className="subjects-list">
              {getAvailableSubjects().map(subject => (
                <div key={subject.id} className="subject-item">
                  <span>{subject.name} ({subject.category})</span>
                  <button onClick={() => handleAssignSubject(subject.id)}>
                    Assign
                  </button>
                </div>
              ))}
            </div>
          )}

          <h4>Assigned Subjects</h4>
          {getClassSubjects().length === 0 ? (
            <p className="no-data">No subjects assigned to this class</p>
          ) : (
            <div className="subjects-list">
              {getClassSubjects().map(subject => (
                <div key={subject.id} className="subject-item">
                  <span>{subject.name} ({subject.category})</span>
                  <button className="remove-button" onClick={() => handleRemoveSubject(subject.id)}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="form-actions">
          <button type="button" onClick={() => setShowSubjectModal(false)}>
            Done
          </button>
        </div>
      </div>
    </div>
  );

  // Subject Teachers modal — shared by the list page and the detail page
  const subjectTeacherModal = showSubjectTeacherModal && selectedClass && (
    <div className="modal-overlay" onClick={() => setShowSubjectTeacherModal(false)}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Subject Teachers - {selectedClass.name}</h3>
        <div className="subject-teacher-management">
          <div className="form-group">
            <label>Home Teacher</label>
            <select
              value={selectedClass.teacherId || ''}
              onChange={async (e) => {
                await updateDoc(doc(db, 'classes', selectedClass.id), {
                  teacherId: e.target.value || null
                });
                dataCacheService.invalidatePath('classes');
                loadClasses();
              }}
            >
              <option value="">Unassigned</option>
              {teachers.map(teacher => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.username}
                </option>
              ))}
            </select>
          </div>

          <h4>Subject Teacher Assignments</h4>
          {availableSubjects.map(subject => (
            <div key={subject} className="subject-teacher-row">
              <span>{subject}</span>
              <select
                value={subjectTeacherAssignments[subject] || ''}
                onChange={async (e) => {
                  const newAssignments = { ...subjectTeacherAssignments };
                  if (e.target.value) {
                    newAssignments[subject] = e.target.value;
                  } else {
                    delete newAssignments[subject];
                  }
                  setSubjectTeacherAssignments(newAssignments);
                  await updateDoc(doc(db, 'classes', selectedClass.id), {
                    subjectTeacherAssignments: newAssignments
                  });
                  dataCacheService.invalidatePath('classes');
                }}
              >
                <option value="">Select Teacher</option>
                {teachers.map(teacher => (
                  <option key={teacher.id} value={teacher.id}>
                    {teacher.username}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
        <div className="form-actions">
          <button type="button" onClick={() => setShowSubjectTeacherModal(false)}>
            Done
          </button>
        </div>
      </div>
    </div>
  );

  // Full-screen class detail page — replaces the list
  if (detailClass) {
    const cls = classes.find(c => c.id === detailClass.id) || detailClass;
    const requiredSubjects = cls.requiredSubjects || [];
    const assignments = cls.subjectTeacherAssignments || {};
    return (
      <div className="admin-classes-view">
        <div className="detail-page">
          <button className="detail-back-button" onClick={() => setDetailClass(null)}>
            ◄ Back to Classes
          </button>

          <div className="detail-hero">
            <div className="detail-avatar">🏫</div>
            <div className="detail-hero-info">
              <h2>{cls.name || 'Unnamed Class'}</h2>
              <p className="detail-subtitle">{cls.grade || 'No grade'} • Home Teacher: {getTeacherName(cls.teacherId)}</p>
              <div className="detail-badges">
                <span className="detail-badge green">{detailLoading ? '…' : detailStudents.length} students</span>
                {requiredSubjects.length > 0 && (
                  <span className="detail-badge gray">{requiredSubjects.length} subjects</span>
                )}
              </div>
            </div>
            <div className="detail-hero-actions">
              <button
                className="detail-btn detail-btn-secondary"
                onClick={() => { setSelectedClass(cls); setShowSubjectModal(true); }}
              >
                📚 Subjects
              </button>
              <button
                className="detail-btn detail-btn-secondary"
                onClick={() => {
                  setSelectedClass(cls);
                  setSubjectTeacherAssignments(cls.subjectTeacherAssignments || {});
                  setShowSubjectTeacherModal(true);
                }}
              >
                👨‍🏫 Subject Teachers
              </button>
              <button
                className="detail-btn detail-btn-edit"
                onClick={() => { setEditClass({ ...cls }); setIsEditing(true); }}
              >
                Edit
              </button>
              <button
                className="detail-btn detail-btn-delete"
                onClick={async () => { if (await handleDeleteClass(cls.id)) setDetailClass(null); }}
              >
                Delete
              </button>
            </div>
          </div>

          <div className="detail-grid">
            <div className="detail-card">
              <h3>Class Information</h3>
              <div className="detail-field">
                <span className="field-label">Class Name</span>
                <span className="field-value">{cls.name || 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Grade Level</span>
                <span className="field-value">{cls.grade || 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Home Teacher</span>
                <span className="field-value">{getTeacherName(cls.teacherId)}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Capacity</span>
                <span className="field-value">{cls.capacity != null ? cls.capacity : 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Student Count</span>
                <span className="field-value">{detailLoading ? '…' : detailStudents.length}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Status</span>
                <span className="field-value">{cls.isActive === false ? 'Inactive' : 'Active'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Created</span>
                <span className="field-value">{cls.createdAt ? new Date(cls.createdAt).toLocaleDateString() : 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Class ID</span>
                <span className="field-value">{cls.id}</span>
              </div>
            </div>

            <div className="detail-card">
              <h3>Subjects &amp; Subject Teachers ({requiredSubjects.length})</h3>
              {requiredSubjects.length === 0 ? (
                <p className="detail-empty">No subjects assigned</p>
              ) : (
                <div className="detail-list">
                  {requiredSubjects.map(subject => {
                    const teacherId = assignments[subject];
                    return (
                      <div key={subject} className="detail-list-item">
                        <span className="item-name">{subject}</span>
                        <span className="item-meta">{teacherId ? getTeacherName(teacherId) : 'No teacher assigned'}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="detail-card full-width">
              <h3>Students ({detailLoading ? '…' : detailStudents.length})</h3>
              {detailLoading ? (
                <p className="detail-empty">Loading students...</p>
              ) : detailStudents.length === 0 ? (
                <p className="detail-empty">No students enrolled</p>
              ) : (
                <div className="detail-list">
                  {detailStudents.map(s => (
                    <div key={s.id} className="detail-list-item">
                      <span className="item-name">{s.fullName || `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'Unknown'}</span>
                      <span className="item-meta">{s.studentId || ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {editClassModal}
        {subjectModal}
        {subjectTeacherModal}
      </div>
    );
  }

  return (
    <div className="admin-classes-view">
      <div className="view-header">
        <h2>Classes</h2>
        <button className="add-button" onClick={() => setShowAddForm(true)}>
          + Add Class
        </button>
      </div>

      <p className="count-text">Classes: {classes.length}</p>

      {showAddForm && (
        <div className="add-class-form">
          <h3>Create New Class</h3>
          <form onSubmit={handleAddClass}>
            <div className="form-group">
              <label>Class Name *</label>
              <input
                type="text"
                value={newClass.name}
                onChange={(e) => setNewClass({ ...newClass, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Grade *</label>
              <select
                value={newClass.grade}
                onChange={(e) => setNewClass({ ...newClass, grade: e.target.value })}
                required
              >
                <option value="">Select Grade</option>
                {gradeLevels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Home Teacher</label>
              <select
                value={newClass.teacherId}
                onChange={(e) => setNewClass({ ...newClass, teacherId: e.target.value })}
              >
                <option value="">Unassigned</option>
                {teachers.map(teacher => (
                  <option key={teacher.id} value={teacher.id}>
                    {teacher.username}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Required Subjects</label>
              <div className="subjects-grid">
                {availableSubjects.map(subject => (
                  <label key={subject} className="subject-checkbox">
                    <input
                      type="checkbox"
                      checked={newClass.requiredSubjects?.includes(subject) || false}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewClass({
                            ...newClass,
                            requiredSubjects: [...(newClass.requiredSubjects || []), subject]
                          });
                        } else {
                          setNewClass({
                            ...newClass,
                            requiredSubjects: (newClass.requiredSubjects || []).filter(s => s !== subject)
                          });
                        }
                      }}
                    />
                    <span>{subject}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button type="submit">Create Class</button>
            </div>
          </form>
        </div>
      )}

      {classes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🏫</div>
          <h3>No Classes</h3>
          <p>Create your first class to get started</p>
        </div>
      ) : (
        <div className="classes-list">
          {classes.map(cls => (
            <div key={cls.id} className="class-card" onClick={() => openClassDetail(cls)} style={{ cursor: 'pointer' }}>
              <div className="class-icon">🏫</div>
              <div className="class-info">
                <h4>{cls.name}</h4>
                <p>{cls.grade}</p>
                <p>Teacher: {getTeacherName(cls.teacherId)}</p>
              </div>
              <div className="class-status">
                <span className="status-dot active"></span>
              </div>
              <span className="class-card-chevron">›</span>
            </div>
          ))}
        </div>
      )}

      {editClassModal}

      {subjectModal}

      {subjectTeacherModal}
    </div>
  );
}

export default AdminClassesView;
